import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useQueryClient } from '@tanstack/react-query'
import {
  ChevronDown,
  Database,
  Orbit,
  Plus,
  ShieldCheck,
  UserRound,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Logo } from './Logo'
import { Button } from './ui/Button'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { authClient } from '@/lib/auth-client'
import { cn } from '@/lib/utils'

// My Workspace and Projects sit in the tab strip on the record pages
// instead, so the masthead stays one link per section of the product.
const NAV = [
  { to: '/database', label: 'Database', icon: Database },
  { to: '/colour-space', label: 'Colour Space', icon: Orbit },
] as const

export function AppShell() {
  const { user, isAdmin } = useCurrentUser()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const handleSignOut = async () => {
    // Better Auth reports failures in the result rather than throwing; navigating
    // regardless would leave a live session behind and bounce the user straight back.
    const { error } = await authClient.signOut()
    if (error) {
      toast.error(error.message ?? 'Could not sign out. Please try again.')
      return
    }
    queryClient.clear()
    navigate('/sign-in', { replace: true })
  }

  return (
    <div className="min-h-screen">
      <header className="border-b border-line bg-paper">
        <div className="mx-auto flex h-14 max-w-[1240px] items-center gap-6 px-5">
          <NavLink to="/database" className="shrink-0">
            <Logo />
          </NavLink>

          <nav className="flex flex-1 items-center justify-center gap-1">
            {NAV.map(({ to, label, icon: Icon }) => (
              <TopLink key={to} to={to} icon={<Icon className="size-4" />}>
                {label}
              </TopLink>
            ))}
            {isAdmin && (
              <TopLink to="/admin" icon={<ShieldCheck className="size-4" />}>
                Admin
              </TopLink>
            )}
          </nav>

          <div className="flex shrink-0 items-center gap-3">
            <Button size="sm" onClick={() => navigate('/database/new')}>
              <Plus className="size-4" />
              New record
            </Button>

            <DropdownMenu.Root>
              <DropdownMenu.Trigger className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-ink-soft transition-colors hover:bg-paper-muted">
                <UserRound className="size-4" />
                <span className="max-w-32 truncate">{user?.name ?? 'Account'}</span>
                <ChevronDown className="size-4 text-ink-faint" />
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="end"
                  sideOffset={6}
                  className="z-50 w-48 overflow-hidden rounded-[var(--radius-control)] border border-line bg-paper py-1 shadow-[var(--shadow-float)]"
                >
                  <MenuLink onSelect={() => navigate('/profile')}>My Profile</MenuLink>
                  <MenuLink onSelect={() => navigate('/workspace/drafts')}>Drafts</MenuLink>
                  <MenuLink onSelect={() => navigate('/change-password')}>Change Password</MenuLink>
                  <DropdownMenu.Separator className="my-1 h-px bg-line" />
                  <MenuLink onSelect={handleSignOut} tone="brand">
                    Log out
                  </MenuLink>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1240px] px-5 py-7">
        <Outlet />
      </main>
    </div>
  )
}

function TopLink({ to, icon, children }: { to: string; icon: ReactNode; children: ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'relative flex items-center gap-1.5 px-3 py-[17px] text-sm font-medium transition-colors',
          isActive
            ? 'text-brand after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:bg-brand'
            : 'text-ink-soft hover:text-ink',
        )
      }
    >
      {icon}
      {children}
    </NavLink>
  )
}

function MenuLink({
  onSelect,
  children,
  tone,
}: {
  onSelect: () => void
  children: ReactNode
  tone?: 'brand'
}) {
  return (
    <DropdownMenu.Item
      onSelect={onSelect}
      className={cn(
        'cursor-pointer px-3 py-2 text-sm outline-none data-[highlighted]:bg-brand-soft',
        tone === 'brand' ? 'text-brand' : 'text-ink-soft',
      )}
    >
      {children}
    </DropdownMenu.Item>
  )
}
